import React from 'react';

const ApproachSection: React.FC = () => {
  const steps = [
    {
      number: '01',
      title: 'Discovery & Analysis',
      description: 'We start by understanding your business needs, challenges, and goals through comprehensive analysis and stakeholder interviews.',
      icon: 'ri-search-line'
    },
    {
      number: '02',
      title: 'Strategy & Planning',
      description: 'Our team develops a tailored strategy and detailed project roadmap that aligns with your business objectives and timeline.',
      icon: 'ri-roadmap-line'
    },
    {
      number: '03',
      title: 'Development & Implementation',
      description: 'We build and implement solutions using agile methodologies, ensuring regular communication and iterative feedback.',
      icon: 'ri-code-line'
    },
    {
      number: '04',
      title: 'Testing & Optimization',
      description: 'Rigorous testing and performance optimization ensure your solution meets the highest quality standards before deployment.',
      icon: 'ri-bug-line'
    },
    {
      number: '05',
      title: 'Deployment & Support',
      description: 'We handle seamless deployment and provide ongoing support to ensure your solution continues to deliver value.',
      icon: 'ri-rocket-line'
    }
  ];

  return (
    <section id="approach" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Our Approach
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            We follow a proven methodology that ensures successful project delivery 
            and maximum value for your investment
          </p>
          <div className="mt-8 bg-teal-500 w-24 h-1 mx-auto rounded-full"></div>
        </div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-teal-200 via-teal-400 to-teal-600 rounded-full"></div>

          <div className="space-y-12 lg:space-y-16">
            {steps.map((step, index) => (
              <div key={index} className={`flex flex-col lg:flex-row items-center gap-8 ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              }`}>
                {/* Content */}
                <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                  <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center text-teal-600 ${
                        index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'
                      }`}>
                        <i className={`${step.icon} text-xl`}></i>
                      </div>
                      <div className={`${index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}`}>
                        <span className="text-sm font-semibold text-teal-600 tracking-wider">
                          STEP {step.number}
                        </span>
                        <h3 className="text-2xl font-bold text-gray-900">
                          {step.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Timeline Node */}
                <div className="hidden lg:block relative z-10">
                  <div className="w-6 h-6 bg-teal-500 rounded-full border-4 border-white shadow-lg"></div>
                </div>

                {/* Spacer */}
                <div className="flex-1 hidden lg:block"></div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">
              Ready to Transform Your Business?
            </h3>
            <p className="text-xl mb-8 text-teal-100">
              Let's discuss how our proven approach can help you achieve your goals
            </p>
            <button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-teal-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg cursor-pointer whitespace-nowrap"
            >
              Start Your Project Today
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApproachSection;